import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import RemarkModal from './RemarkModal';
import LogsModal from './LogsModal';
import { useAuth } from '../context/AuthContext';
import NetSuiteWarnings from './NetSuiteWarnings';
import Select from 'react-select';
import ShippingAddressSelector from './ShippingAddressSelector';
import { API_URL } from '../config/api';

const EditOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  // SSO refreshes the profile object every 30 seconds. Only identity changes
  // should reload the editable order; profile/role refreshes must keep drafts.
  const userId = currentUser?.id;
  const staffCode = currentUser?.staff_code;
  const employeeId = currentUser?.employee_id;

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [orderStatus, setOrderStatus] = useState('');
  const [orderLocked, setOrderLocked] = useState(false);
  const [formData, setFormData] = useState({
    memo: '',
    otherrefnum: '',
    tranDate: '',
    location_id: '',
    custbody_ar_req_inv_mac5: '',
    shipaddresslist: '',
    custbodyar_so_memo2: '',
    custbody_ar_all_memo: '',
    custbody_ar_estimate_contrat1: '',
    items: []
  });
  const [logs, setLogs] = useState([]);
  const [netsuiteWarnings, setNetsuiteWarnings] = useState([]);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [collapseStates, setCollapseStates] = useState({});
  const [isMyOrder, setIsMyOrder] = useState(false);
  const [itemsViewMode, setItemsViewMode] = useState('card');
  const [allCollapsed, setAllCollapsed] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [activeSection, setActiveSection] = useState('overview');
  const [itemUnitsMap, setItemUnitsMap] = useState({});


  // File states
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);

  // Summary calculations
  const [orderSummary, setOrderSummary] = useState({
    totalItems: 0,
    totalQuantity: 0,
    totalAmount: 0,
    averageRate: 0
  });


  const LocationSelect = ({ value, onChange, disabled, locations, size = 'normal' }) => {
    const selectClass = size === 'small' ? 'form-select form-select-sm' : 'form-select';
    const hasLocations = Object.entries(locations).length > 0;
    const currentLocationExists = value && locations[value];
    
    return (
      <>
        <select
          className={selectClass}
          value={value}
          onChange={onChange}
          disabled={disabled}
          style={disabled ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
        >
          <option value="">เลือกสถานที่</option>
          {/* Show current location even if not in user's selected locations (read-only) */}
          {value && !currentLocationExists && orderData?.originalLocations && (
            <option value={value} style={{ color: '#6c757d', fontStyle: 'italic' }}>
              {orderData.originalLocations[value] || `Location ${value}`} (ปัจจุบัน)
            </option>
          )}
          {!hasLocations && !value ? (
            <option value="" disabled>ไม่มีสถานที่ที่เลือกไว้</option>
          ) : (
            Object.entries(locations).map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))
          )}
        </select>
        {!hasLocations && !value && (
          <small className="text-warning mt-1 d-block">
            <i className="bi bi-exclamation-triangle me-1"></i>
            <Link to="/profile" className="text-warning text-decoration-underline">
              ไปที่โปรไฟล์เพื่อเลือกสถานที่
            </Link>
          </small>
        )}
      </>
    );
  };
  

  const handleMainLocationChange = (newLocationId) => {
  if (orderLocked) {
    toast.error('ไม่สามารถแก้ไขได้ เนื่องจากสถานะคำสั่งซื้อเป็น: ' + orderStatus);
    return;
  }

  // Show confirmation dialog
  const itemCount = formData.items.length;
  const confirmMessage = `คุณต้องการเปลี่ยนสถานที่สำหรับรายการสินค้าทั้งหมด ${itemCount} รายการหรือไม่?\n\nสถานที่ใหม่: ${orderData?.locations?.[newLocationId] || newLocationId}\n\nรายการสินค้าแต่ละรายการยังสามารถเปลี่ยนแปลงได้อีกครั้งภายหลัง`;
  
  if (window.confirm(confirmMessage)) {
    // Update main location and all items' locations
    setFormData(prev => ({
      ...prev,
      location_id: newLocationId,
      // Update all items' locations
      items: prev.items.map(item => ({
        ...item,
        location: newLocationId
      }))
    }));
    
    toast.success(`อัปเดตสถานที่สำหรับรายการสินค้าทั้งหมด ${itemCount} รายการแล้ว`);
  }
};
  useEffect(() => {
    let active = true;
    const fetchOrder = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${API_URL}/order/${id}`);
        if (!active) return;
        setOrderData(response.data);
        const so = response.data.so;
        console.log('🏠 NetSuite SO shipping fields:', {
        shipAddress: so.shipAddress,
        shipaddresslist: so.shipaddresslist,
        shipAddress_text: so.shipAddress_text,
        shipaddress: so.shipaddress
      });
        // Get and check status
        const currentStatus = so.orderStatus?.refName || 'Unknown';
        setOrderStatus(currentStatus);
        
        // Check if order is editable
        const isEditable = currentStatus === 'Pending Approval';
        setOrderLocked(!isEditable);
        
        if (!isEditable) {
          toast.warning(`ไม่สามารถแก้ไขได้ สถานะปัจจุบัน: ${currentStatus}`);
        }

// Set the itemUnitsMap from response
        if (response.data.itemUnitsMap) {
          setItemUnitsMap(response.data.itemUnitsMap);
          console.log('📊 itemUnitsMap received:', response.data.itemUnitsMap);
          console.log('📊 Units for item 44815:', response.data.itemUnitsMap['44815']);
        } else {
          console.log('❌ No itemUnitsMap in response');
        }

        // Check order ownership
        if (staffCode) {
          try {
            const conn = await axios.get(`${API_URL}/orders/staff/${staffCode}`);
            if (!active) return;
            const myOrders = conn.data.orders || [];
            const matchingOrder = myOrders.find(order => order.netsuite_id === id);
            setIsMyOrder(!!matchingOrder);
          } catch (error) {
            console.error('Error checking order ownership:', error);
          }
        }

        // Fetch filtered locations for current user
        let filteredLocations = response.data.locations; // Default to all locations
        if (employeeId) {
          try {
            const locResponse = await axios.get(`${API_URL}/user-locations/${employeeId}/filtered`);
            if (locResponse.data.success && Object.keys(locResponse.data.locations).length > 0) {
              filteredLocations = locResponse.data.locations;
            } else {
              // If user has no selected locations, they get empty dropdown
              filteredLocations = {};
            }
          } catch (error) {
            console.error('Error fetching user locations:', error);
            // Fall back to all locations if there's an error
          }
        }

        if (!active) return;
        const initialFormData = {
          memo: so.memo || '',
          otherrefnum: so.otherRefNum || '',
          tranDate: so.tranDate ? so.tranDate.substring(0, 10) : '',
          location_id: so.location?.id || '',
          custbody_ar_req_inv_mac5: so.custbody_ar_req_inv_mac5 || '',
          shipaddresslist: so?.shipAddressList?.id || '', // Use the ID from NetSuite
          currentShipAddress: so?.shipAddress || '',
          custbodyar_so_memo2: so.custbodyar_so_memo2 || '',
          custbody_ar_all_memo: so.custbody_ar_all_memo || '',
          custbody_ar_estimate_contrat1: so.custbody_ar_estimate_contrat1 || '',
          items: response.data.items.map(item => ({
            href: item.href,
            item_id: item.item?.id || '',
            quantity: item.quantity || '',
            rate: item.rate || '',
            description: item.description || '',
            location: item.inventorylocation?.id || '',
            custcol_ar_so_custprice: item.custcol_ar_so_custprice || '',
            custcol_ice_ld_discount: item.custcol_ice_ld_discount || '',
            custcol_ice_ld_originalrate: item.custcol_ice_ld_originalrate || '',
            custcol_ice_ld_totalafterdiscount: item.custcol_ice_ld_totalafterdiscount || '',
            units: item.units || '',
            units_display: item.units_display || ''
          }))
        };

        

        setFormData(initialFormData);
        calculateOrderSummary(initialFormData.items);

        // Update orderData with filtered locations and keep original for reference
        setOrderData(prevData => ({
          ...prevData,
          locations: filteredLocations,
          originalLocations: response.data.locations // Keep original for existing item locations
        }));

        // Initialize all items as expanded
        const initialCollapseStates = {};
        response.data.items.forEach((item, i) => {
          initialCollapseStates[i] = false;
        });
        setCollapseStates(initialCollapseStates);

        setLoading(false);
      } catch (err) {
        if (!active) return;
        console.error('Error fetching order:', err);

        // NetSuite slow / timeout — either our backend 503 message, or a raw
        // 504 from the proxy / an axios timeout that never reached the backend.
        const status = err.response?.status;
        const isNetSuiteSlow =
          err.response?.data?.netsuiteSlow ||
          status === 502 || status === 503 || status === 504 ||
          err.code === 'ECONNABORTED' ||
          /timeout/i.test(err.message || '');

        if (isNetSuiteSlow) {
          setError(err.response?.data?.message ||
            'ระบบ NetSuite ตอบสนองช้าในขณะนี้ กรุณาลองใหม่อีกครั้งในอีกสักครู่');
        } else if (status === 404) {
          setError(err.response?.data?.message || 'ไม่พบ Sales Order นี้ในระบบ NetSuite');
        } else {
          setError('Failed to load order data');
        }
        setLoading(false);
      }
    };

    fetchOrder();
    return () => { active = false; };
  }, [id, userId, staffCode, employeeId]);

  const handleUnitsChange = (index, newUnitsId) => {
  if (orderLocked) {
    toast.error('ไม่สามารถแก้ไขได้ เนื่องจากสถานะคำสั่งซื้อเป็น: ' + orderStatus);
    return;
  }

  // Find the unit name for display
  const itemId = formData.items[index].item_id;
  const recommendedUnits = itemUnitsMap[itemId] || [];
  const selectedUnit = recommendedUnits.find(unit => unit.id == newUnitsId);
  const displayName = selectedUnit ? selectedUnit.name : newUnitsId;

  const updatedItems = [...formData.items];
  updatedItems[index] = {
    ...updatedItems[index],
    units: newUnitsId, // Store Internal_ID
    units_display: displayName // Store display name
  };

  setFormData({
    ...formData,
    items: updatedItems
  });
};
const formatAddressForDisplay = (address) => {
  const parts = [
    address.address_1,
    address.address_2,
    address.city,
    address.zip_code,
    address.country
  ].filter(part => part && part.trim() !== '');
  
  return parts.join(', ');
};
const UnitsSelect = ({ value, onChange, disabled, itemId, size = 'normal' }) => {
  const recommendedUnits = itemUnitsMap[itemId] || [];
  
  const options = recommendedUnits.map(unit => ({
    value: unit.id,
    label: `${unit.name}`,
  }));
  
  // ADD THIS: If no recommended units but we have a current value, show it
  const currentItem = formData.items.find(item => item.item_id === itemId);
  if (options.length === 0 && value && currentItem?.units_display) {
    options.push({
      value: value,
      label: `${currentItem.units_display} (current)`,
    });
  }
  
  const selectedOption = options.find(opt => opt.value == value) || null;

  
  const h = size === 'small' ? '31px' : '38px';
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: h,
      height: h,
      fontSize: size === 'small' ? '0.875rem' : '1rem',
      backgroundColor: disabled ? '#f8f9fa' : 'white',
      cursor: disabled ? 'not-allowed' : 'default',
      flexWrap: 'nowrap',
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: h,
      padding: '0 8px',
      flexWrap: 'nowrap',
      overflow: 'hidden',
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
    }),
    singleValue: (provided) => ({
      ...provided,
      marginTop: 0,
      marginBottom: 0,
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: h,
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: '0 4px',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: '0 6px',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999
    })
  };
  
  return (
    <Select
      value={selectedOption}
      onChange={(selected) => onChange(selected ? selected.value : '')}
      options={options}
      isDisabled={disabled}
      isClearable={true}
      isSearchable={true}
      placeholder="เลือกหน่วย..."
      noOptionsMessage={() => "ไม่พบหน่วยที่แนะนำ"}
      styles={customStyles}
      menuPortalTarget={document.body}
    />
  );
};

useEffect(() => {
  const fetchDepartments = async () => {
    try {
      const res = await axios.get(`${API_URL}/departments`);
      if (res.data.success) {
        const filtered = res.data.departments
          .map(d => d.Name_no_hierarchy)
          .filter(name => name.startsWith('S'))
          .map(name => ({ label: name, value: name }));
        setDepartments(filtered);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };
  fetchDepartments();
}, []);

  useEffect(() => {
    if (id) {
      fetchAttachedFiles();
    }
  }, [id]);

  // Calculate order summary whenever items change
  useEffect(() => {
    calculateOrderSummary(formData.items);
  }, [formData.items]);

  const calculateOrderSummary = (items) => {
    const totalItems = items.length;
    const totalQuantity = items.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
    const totalAmount = items.reduce((sum, item) => sum + (parseFloat(item.quantity || 0) * parseFloat(item.rate || 0)), 0);
    const averageRate = totalQuantity > 0 ? totalAmount / totalQuantity : 0;

    setOrderSummary({
      totalItems,
      totalQuantity,
      totalAmount,
      averageRate
    });
  };

  const fetchAttachedFiles = async () => {
    try {
      const response = await axios.get(`${API_URL}/order/${id}/files`);
      if (response.data.success) {
        setAttachedFiles(response.data.files);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const handleFileSelect = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };

  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('กรุณาเลือกไฟล์');
      return;
    }

    if (orderLocked) {
      toast.error('ไม่สามารถอัปโหลดไฟล์ได้ เนื่องจากคำสั่งซื้อถูกล็อค');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      
      selectedFiles.forEach(file => {
        formData.append('pdfFile', file);
      });

      const response = await axios.post(`${API_URL}/order/${id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success(`อัปโหลด ${response.data.files.length} ไฟล์สำเร็จ`);
        setSelectedFiles([]);
        document.getElementById('fileInput').value = '';
        fetchAttachedFiles();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('เกิดข้อผิดพลาดในการอัปโหลด');
    } finally {
      setUploading(false);
    }
  };

  const handleViewFile = (filename) => {
    window.open(`${API_URL}/order/${id}/file/${filename}`, '_blank');
  };

  const handleDeleteFile = async (filename) => {
    if (orderLocked) {
      toast.error('ไม่สามารถลบไฟล์ได้ เนื่องจากคำสั่งซื้อถูกล็อค');
      return;
    }

    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะลบไฟล์นี้?')) return;

    try {
      await axios.delete(`${API_URL}/order/${id}/file/${filename}`);
      toast.success('ลบไฟล์สำเร็จ');
      fetchAttachedFiles();
    } catch (error) {
      toast.error('เกิดข้อผิดพลาดในการลบไฟล์');
    }
  };

  const getFileIcon = (type) => {
    return type === 'pdf' ? 
      <i className="bi bi-file-earmark-pdf text-danger"></i> : 
      <i className="bi bi-file-earmark-image text-success"></i>;
  };

  const handleInputChange = (e) => {
    if (orderLocked) {
      toast.error('ไม่สามารถแก้ไขได้ เนื่องจากสถานะคำสั่งซื้อเป็น: ' + orderStatus);
      return;
    }

    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleItemChange = (index, field, value) => {
    if (orderLocked) {
      toast.error('ไม่สามารถแก้ไขได้ เนื่องจากสถานะคำสั่งซื้อเป็น: ' + orderStatus);
      return;
    }

    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };

    setFormData({
      ...formData,
      items: updatedItems
    });
  };

  const handleDepartmentChange = (e) => {
    if (orderLocked) {
      toast.error('ไม่สามารถแก้ไขได้ เนื่องจากสถานะคำสั่งซื้อเป็น: ' + orderStatus);
      return;
    }

    const options = Array.from(e.target.selectedOptions);
    setSelectedDepartments(options.map(o => o.value));
  };

  const toggleCollapse = (index) => {
    setCollapseStates(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleCollapseAll = (collapse = true) => {
    const newStates = {};
    formData.items.forEach((item, i) => {
      newStates[i] = collapse;
    });
    setCollapseStates(newStates);
    setAllCollapsed(collapse);
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  if (orderLocked) {
    toast.error(`ไม่สามารถบันทึกได้ สถานะปัจจุบัน: ${orderStatus}`);
    return;
  }

  try {
    setProcessing(true);
    const payload = { ...formData, selectedDepartments, updatedBy: currentUser?.id || null };
    const response = await axios.post(`${API_URL}/order/${id}/update`, payload);

    setLogs(response.data.logs);
    
    // Handle warnings if present
    if (response.data.warnings && response.data.warnings.length > 0) {
      setNetsuiteWarnings(response.data.warnings);
      toast.warning(`Sales Order updated with ${response.data.warnings.length} NetSuite warning(s). Please review them above.`, {
        autoClose: 5000
      });
    } else {
      setNetsuiteWarnings([]);
    }
    
    const notificationSent = response.data.logs.some(log => 
      log.includes('ส่งการแจ้งเตือน')
    );
    
    if (response.data.headerUpdated || response.data.itemsUpdated.length > 0) {
      if (notificationSent) {
        toast.success('Sales Order updated successfully. Notifications sent to coordination team!', {
          autoClose: 5000
        });
      } else {
        toast.success('Sales Order updated successfully!');
      }
    } else {
      toast.info('No changes were detected in the sales order.');
    }
    
    setShowLogsModal(true);
    setProcessing(false);
    
  } catch (err) {
    console.error('Error updating order:', err);
    
    // Check if it's a status change error
    if (err.response?.status === 403 && err.response?.data?.statusChanged) {
      toast.error('สถานะของเอกสารบน NetSuite ถูกเปลี่ยนแล้ว ไม่สามารถแก้ไขได้', {
        autoClose: 5000
      });
    } else {
      // Other errors
      toast.error('Failed to update Sales Order: ' + (err.response?.data?.message || err.message));
    }
    
    setProcessing(false);
  }
};

  const handleRemarkSave = (remarkText) => {
    if (orderLocked) {
      toast.error('ไม่สามารถแก้ไขได้ เนื่องจากสถานะคำสั่งซื้อเป็น: ' + orderStatus);
      return;
    }

    setFormData({
      ...formData,
      custbodyar_so_memo2: remarkText
    });
    setShowRemarkModal(false);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Pending Approval':
        return 'bg-warning text-dark';
      case 'Approved':
        return 'bg-success';
      case 'Rejected':
        return 'bg-danger';
      case 'Pending Fulfillment':
        return 'bg-info';
      case 'Pending Billing':
        return 'bg-primary';
      default:
        return 'bg-secondary';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (loading) {
    return (
      <div className="page-loader">
        <div className="page-loader__content">
          <img src="/VERP.png" alt="VERP" className="page-loader__logo" />
          <div className="page-loader__bar">
            <div className="page-loader__bar-fill"></div>
          </div>
          <p className="page-loader__message">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid py-5">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8">
            <div className="card border-0 shadow-lg">
              <div className="card-body text-center py-5">
                <div className="mb-4">
                  <div className="rounded-circle bg-warning bg-opacity-10 d-inline-flex align-items-center justify-content-center" style={{ width: '80px', height: '80px' }}>
                    <i className="bi bi-exclamation-triangle-fill text-warning" style={{ fontSize: '2.5rem' }}></i>
                  </div>
                </div>
                <h3 className="mb-3 fw-bold">
                  {error && error.includes('NetSuite') ? 'NetSuite ตอบสนองช้า' : 'ไม่พบข้อมูล Sales Order'}
                </h3>
                <p className="text-muted mb-4">{error}</p>
                <button onClick={() => navigate(-1)} className="btn btn-primary btn-lg px-4">
                  <i className="bi bi-arrow-left me-2"></i> กลับไปหน้าก่อนหน้า
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!orderData) return null;

  const { so, venioSONumber, customerName, shippingAddresses, locations } = orderData;

  return (
    <div className="theme-container theme-bg-page">
      <div className="row g-3">
        <div className="col-12">
          {/* Header Section */}
          <div className="theme-page-header" id="overview">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <h1 className="theme-page-title mb-0">Sales Order #{id}</h1>
                <span className={`theme-badge ${getStatusBadgeClass(orderStatus).includes('warning') ? 'theme-badge-warning' : getStatusBadgeClass(orderStatus).includes('success') ? 'theme-badge-success' : getStatusBadgeClass(orderStatus).includes('danger') ? 'theme-badge-danger' : 'theme-badge-primary'}`}>
                  {orderStatus}
                </span>
                {orderLocked && <span className="theme-badge theme-badge-danger"><i className="bi bi-lock-fill me-1"></i>ล็อค</span>}
                {isMyOrder && <span className="theme-badge theme-badge-primary"><i className="bi bi-person-check-fill me-1"></i>คำสั่งซื้อของคุณ</span>}
              </div>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                {venioSONumber && (
                  <span className="theme-text-secondary" style={{ fontSize: '13px' }}>
                    <i className="bi bi-link-45deg me-1"></i>Venio SO: <strong>{venioSONumber}</strong>
                  </span>
                )}
                <span className="theme-text-secondary" style={{ fontSize: '13px' }}>
                  <i className="bi bi-building me-1"></i>{customerName}
                </span>
              </div>
            </div>
          </div>

          {/* Lock Warning Banner */}
          {orderLocked && (
            <div className="alert alert-warning border-warning d-flex align-items-center gap-2 py-2 mb-4" style={{ fontSize: '13px' }}>
              <i className="bi bi-lock-fill text-warning"></i>
              <span>คำสั่งซื้อนี้มีสถานะ <strong>{orderStatus}</strong> — ไม่สามารถแก้ไขได้</span>
            </div>
          )}

          {netsuiteWarnings.length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <NetSuiteWarnings 
                warnings={netsuiteWarnings} 
                onDismiss={() => setNetsuiteWarnings([])}
              />
            </div>
          </div>
        )}

          <form onSubmit={handleSubmit}>
            {/* Order Summary Bar */}
            <div className="theme-card mb-3 d-flex align-items-center gap-0" id="summary" style={{ overflow: 'hidden' }}>
              <div className="d-flex align-items-center gap-2 px-3 py-2 border-end" style={{ flex: 1 }}>
                <i className="bi bi-cart-fill text-primary"></i>
                <div>
                  <div style={{ fontSize: '10px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>รายการสินค้า</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1.2 }}>{orderSummary.totalItems}</div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2 px-3 py-2 border-end" style={{ flex: 1 }}>
                <i className="bi bi-boxes text-info"></i>
                <div>
                  <div style={{ fontSize: '10px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>จำนวนรวม</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1.2 }}>{orderSummary.totalQuantity.toFixed(0)}</div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2 px-3 py-2" style={{ flex: 2 }}>
                <i className="bi bi-currency-dollar text-success"></i>
                <div>
                  <div style={{ fontSize: '10px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>มูลค่ารวม</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--color-success)', lineHeight: 1.2 }}>{formatCurrency(orderSummary.totalAmount)}</div>
                </div>
              </div>
            </div>

            {/* Order Main Info */}
            <div className="row mb-3" id="main-info">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="theme-card-header">
                    <i className="bi bi-info-circle"></i>
                    <span>ข้อมูลหลัก</span>
                  </div>
                  <div className="card-body p-3">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          <i className="bi bi-pencil text-primary me-2"></i>Memo
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="custbody_ar_all_memo"
                          value={formData.custbody_ar_all_memo}
                          onChange={handleInputChange}
                          readOnly={orderLocked}
                          disabled={orderLocked}
                          style={orderLocked ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          <i className="bi bi-hash text-primary me-2"></i>PO #
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="otherrefnum"
                          value={formData.otherrefnum}
                          onChange={handleInputChange}
                          readOnly={orderLocked}
                          disabled={orderLocked}
                          style={orderLocked ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          <i className="bi bi-calendar-date text-primary me-2"></i>Tran Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          name="tranDate"
                          value={formData.tranDate}
                          onChange={handleInputChange}
                          readOnly={orderLocked}
                          disabled={orderLocked}
                          style={orderLocked ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                        />
                      </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold">
                            <i className="bi bi-geo-alt text-primary me-2"></i>Location (Default for all items)
                          </label>
                          <LocationSelect
                            value={formData.location_id}
                            onChange={(e) => handleMainLocationChange(e.target.value)}
                            disabled={orderLocked}
                            locations={orderData?.locations || {}}
                            size="normal"
                          />
                          <small className="text-muted mt-1 d-block">
                            <i className="bi bi-info-circle me-1"></i>
                            การเปลี่ยนแปลงจะส่งผลต่อรายการสินค้าทั้งหมด (แต่ยังสามารถแก้ไขรายการเดี่ยวได้)
                          </small>
                          {Object.keys(orderData?.locations || {}).length === 0 && (
                            <small className="text-warning mt-1 d-block">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              <Link to="/profile" className="text-warning text-decoration-underline">
                                ไปที่โปรไฟล์เพื่อเลือกสถานที่
                              </Link>
                            </small>
                          )}
                        </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          <i className="bi bi-file-text text-primary me-2"></i>Req Inv MAC5
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="custbody_ar_req_inv_mac5"
                          value={formData.custbody_ar_req_inv_mac5}
                          onChange={handleInputChange}
                          readOnly={orderLocked}
                          disabled={orderLocked}
                          style={orderLocked ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          <i className="bi bi-person text-primary me-2"></i>Customer
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={customerName}
                          readOnly
                        />
                      </div>
<div className="col-12">
  <ShippingAddressSelector
    shippingAddresses={shippingAddresses}
    selectedAddressId={formData.shipaddresslist}
    onAddressSelect={(addressId) => {
      if (orderLocked) {
        toast.error('ไม่สามารถแก้ไขได้ เนื่องจากสถานะคำสั่งซื้อเป็น: ' + orderStatus);
        return;
      }
      setFormData(prev => ({
        ...prev,
        shipaddresslist: addressId
      }));
    }}
    currentShipAddress={formData.currentShipAddress}
    disabled={orderLocked}
  />
</div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          <i className="bi bi-clipboard-data text-primary me-2"></i>ผู้ติดต่อ
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="custbody_ar_estimate_contrat1"
                          value={formData.custbody_ar_estimate_contrat1}
                          onChange={handleInputChange}
                          readOnly={orderLocked}
                          disabled={orderLocked}
                          style={orderLocked ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold">
                          <i className="bi bi-chat-text text-primary me-2"></i>Remark (Long Memo)
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            value={formData.custbodyar_so_memo2}
                            readOnly
                            onClick={() => !orderLocked && setShowRemarkModal(true)}
                            style={orderLocked ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : { cursor: 'pointer' }}
                            placeholder="คลิกเพื่อเพิ่มหมายเหตุ..."
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => !orderLocked && setShowRemarkModal(true)}
                            disabled={orderLocked}
                          >
                            <i className="bi bi-pencil-square"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* File Upload Section */}
            <div className="row mb-3" id="files">
              <div className="col-12">
                <div className="theme-card">
                  <div className="theme-card-header">
                    <i className="bi bi-paperclip"></i>
                    <span>ไฟล์แนบ</span>
                    <span className="theme-badge theme-badge-primary ms-1">{attachedFiles.length}</span>
                  </div>
                  <div className="card-body p-3">

                    {/* Upload Section */}
                    <div className="row mb-3">
                      <div className="col-md-8">
                        <div className="input-group">
                          <input
                            type="file"
                            className="form-control"
                            id="fileInput"
                            accept=".pdf,.jpg,.jpeg,.png,.gif"
                            multiple
                            onChange={handleFileSelect}
                            disabled={uploading || orderLocked}
                          />
                          <button
                            type="button"
                            className="btn btn-info"
                            onClick={handleFileUpload}
                            disabled={selectedFiles.length === 0 || uploading || orderLocked}
                          >
                            {uploading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                กำลังอัปโหลด...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-cloud-upload me-2"></i>
                                อัปโหลด ({selectedFiles.length})
                              </>
                            )}
                          </button>
                        </div>
                        <small className="text-muted mt-2 d-block">
                          <i className="bi bi-info-circle me-1"></i>
                          รองรับ PDF และรูปภาพ ขนาดไม่เกิน 10MB
                          {orderLocked && <span className="text-danger ms-2">(ไม่สามารถอัปโหลดได้เนื่องจากคำสั่งซื้อถูกล็อค)</span>}
                        </small>
                      </div>
                    </div>

                    {/* Attached Files */}
                    {attachedFiles.length > 0 ? (
                      <div>
                        <div className="row g-2">
                          {attachedFiles.map((file, index) => (
                            <div key={index} className="col-md-6 col-lg-4">
                              <div className="card h-100 border-0 shadow-sm">
                                <div className="card-body p-2 d-flex align-items-center gap-2">
                                  <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>
                                    {getFileIcon(file.type)}
                                  </div>
                                  <div className="flex-grow-1 min-w-0">
                                    <div className="text-truncate small fw-semibold">{file.filename}</div>
                                    <div className="text-muted" style={{ fontSize: '11px' }}>{file.type.toUpperCase()}</div>
                                  </div>
                                  <div className="d-flex gap-1 flex-shrink-0">
                                    <button
                                      type="button"
                                      className="btn btn-outline-primary btn-sm px-2"
                                      onClick={() => handleViewFile(file.filename)}
                                      title="ดูไฟล์"
                                    >
                                      <i className="bi bi-eye"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm px-2"
                                      onClick={() => handleDeleteFile(file.filename)}
                                      disabled={orderLocked}
                                      title="ลบไฟล์"
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-muted small py-2">
                        <i className="bi bi-inbox me-1"></i>ยังไม่มีไฟล์แนบ
                      </div>
                    )}
                    
                  </div>
                </div>
              </div>
            </div>

            {/* Items Section */}
            <div className="row mb-3" id="items">
              <div className="col-12">
                <div className="theme-card">
                  <div className="theme-card-header">
                    <div className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-cart"></i>
                        <span>รายการสินค้า</span>
                        <span className="theme-badge theme-badge-success">{formData.items.length}</span>
                        {orderLocked && <span className="theme-badge theme-badge-danger"><i className="bi bi-lock-fill me-1"></i>ล็อค</span>}
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <div className="btn-group" role="group">
                          <button
                            type="button"
                            className={`btn btn-sm ${itemsViewMode === 'card' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setItemsViewMode('card')}
                          >
                            <i className="bi bi-grid-3x3-gap me-1"></i> Card
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm ${itemsViewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setItemsViewMode('table')}
                          >
                            <i className="bi bi-table me-1"></i> Table
                          </button>
                        </div>
                        
                        {itemsViewMode === 'card' && (
                          <div className="btn-group" role="group">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleCollapseAll(false)}
                              disabled={!allCollapsed}
                            >
                              <i className="bi bi-arrows-angle-expand me-1"></i> Expand
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleCollapseAll(true)}
                              disabled={allCollapsed}
                            >
                              <i className="bi bi-arrows-angle-contract me-1"></i> Collapse
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="card-body p-3">
                    {/* Items Content */}
                {itemsViewMode === 'card' ? (
                  <>
                    {formData.items.length === 0 && (
                      <div className="alert alert-light border text-center py-4">
                        <i className="bi bi-cart-x text-muted" style={{ fontSize: '2rem' }}></i>
                        <h6 className="mt-2 text-muted">ไม่มีรายการสินค้า</h6>
                      </div>
                    )}
                    {formData.items.map((item, i) => {
                      const itemDisplay = orderData.itemMap[item.item_id] || item.item_id;
                      const itemTotal = parseFloat(item.quantity || 0) * parseFloat(item.rate || 0);
                      return (
                        <div
                          className="card mb-3 border-0 shadow-sm"
                          key={i}
                          style={{ 
                            borderLeft: '4px solid #0d6efd', 
                            transition: 'all 0.3s ease',
                            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                          }}
                        >
                          <div
                            className="card-header bg-transparent border-0 d-flex align-items-center py-2"
                            onClick={() => toggleCollapse(i)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="d-flex align-items-center flex-grow-1 gap-2">
                              <span className="badge bg-primary rounded-pill">{i + 1}</span>
                              <div>
                                <div className="fw-semibold small">{item.description || `Item ${i + 1}`}</div>
                                <div className="text-muted" style={{ fontSize: '11px' }}>{itemDisplay}</div>
                              </div>
                            </div>
                            <div className="d-flex align-items-center">
                              <div className="text-end me-3">
                                <div className="fw-bold text-success">{formatCurrency(itemTotal)}</div>
                                <small className="text-muted">{item.quantity} × {formatCurrency(parseFloat(item.rate || 0))}</small>
                              </div>
                              <i className={`bi ${collapseStates[i] ? 'bi-chevron-up' : 'bi-chevron-down'} text-primary`}></i>
                            </div>
                          </div>
                          {!collapseStates[i] && (
                            <div className="pb-3 px-3">
                              <div className="row g-2">
                                {/* Row 1: Item code, Description, Qty, Units */}
                                <div className="col-md-2">
                                  <div className="small text-muted mb-1">Item Code</div>
                                  <div className="form-control bg-light text-truncate" style={{ cursor: 'default' }} title={itemDisplay}>{itemDisplay}</div>
                                </div>
                                <div className="col-md-6">
                                  <div className="small text-muted mb-1">Description</div>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={item.description}
                                    onChange={e => handleItemChange(i, 'description', e.target.value)}
                                    readOnly={orderLocked}
                                    disabled={orderLocked}
                                    style={orderLocked ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                                  />
                                </div>
                                <div className="col-md-1">
                                  <div className="small text-muted mb-1">Qty</div>
                                  <div className="form-control bg-light text-end">{item.quantity}</div>
                                </div>
                                <div className="col-md-3">
                                  <div className="small text-muted mb-1">Units</div>
                                  <UnitsSelect
                                    value={item.units}
                                    onChange={(newValue) => handleUnitsChange(i, newValue)}
                                    disabled={orderLocked}
                                    itemId={item.item_id}
                                    size="normal"
                                  />
                                </div>

                                {/* Row 2: Pricing (read-only) + Location */}
                                <div className="col-md-2">
                                  <div className="small text-muted mb-1">Original Rate</div>
                                  <div className="form-control bg-light text-end">{formatCurrency(parseFloat(item.custcol_ice_ld_originalrate || 0))}</div>
                                </div>
                                <div className="col-md-2">
                                  <div className="small text-muted mb-1">Final Rate</div>
                                  <div className="form-control bg-light text-end">{formatCurrency(parseFloat(item.rate || 0))}</div>
                                </div>
                                <div className="col-md-2">
                                  <div className="small text-muted mb-1">Customer Price</div>
                                  <div className="form-control bg-light text-end">{formatCurrency(parseFloat(item.custcol_ar_so_custprice || 0))}</div>
                                </div>
                                <div className="col-md-1">
                                  <div className="small text-muted mb-1">Disc %</div>
                                  <div className="form-control bg-light text-end">{item.custcol_ice_ld_discountpercent ? `${parseFloat(item.custcol_ice_ld_discountpercent).toFixed(1)}%` : '0%'}</div>
                                </div>
                                <div className="col-md-2">
                                  <div className="small text-muted mb-1">Disc Amount</div>
                                  <div className="form-control bg-light text-end">{formatCurrency(parseFloat(item.custcol_ice_ld_discountperunit || 0))}</div>
                                </div>
                                <div className="col-md-3">
                                  <div className="small text-muted mb-1">Location</div>
                                  <LocationSelect
                                    value={item.location}
                                    onChange={e => handleItemChange(i, 'location', e.target.value)}
                                    disabled={orderLocked}
                                    locations={orderData?.locations || {}}
                                    size="normal"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th className="fw-semibold">#</th>
                          <th className="fw-semibold">Item</th>
                          <th className="fw-semibold">Qty</th>
                          <th className="fw-semibold">Original Rate</th>
                          <th className="fw-semibold">Final Rate</th>
                          <th className="fw-semibold">Customer Price</th>
                          <th className="fw-semibold">Discount Amount</th>
                          <th className="fw-semibold">Discount %</th>
                          <th className="fw-semibold">Discount Per Unit</th>
                          <th className="fw-semibold">Description</th>
                          <th className="fw-semibold">Location</th>
                          <th className="fw-semibold">Item Total</th>
                          <th className="fw-semibold">Units 11</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.length === 0 ? (
                          <tr>
                            <td colSpan={13} className="text-center text-muted py-4">
                              <i className="bi bi-cart-x" style={{ fontSize: '2rem' }}></i>
                              <div className="mt-2">ไม่มีรายการสินค้า</div>
                            </td>
                          </tr>
                        ) : (
                          formData.items.map((item, i) => {
                            const itemDisplay = orderData.itemMap[item.item_id] || item.item_id;
                            const itemTotal = parseFloat(item.quantity || 0) * parseFloat(item.rate || 0);
                            return (
                              <tr key={i}>
                                <td>
                                  <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white fw-bold" style={{ width: '30px', height: '30px' }}>
                                    {i + 1}
                                  </div>
                                </td>
                                <td style={{ minWidth: '120px' }}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={itemDisplay}
                                    readOnly
                                  />
                                </td>
                                <td style={{ minWidth: '80px' }}>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm text-end"
                                    value={item.quantity}
                                    onChange={e => handleItemChange(i, 'quantity', e.target.value)}
                                    readOnly={orderLocked}
                                    disabled={orderLocked}
                                    style={orderLocked ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                                    step="0.01"
                                  />
                                </td>
                                <td style={{ minWidth: '100px' }}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm text-end bg-light"
                                    value={formatCurrency(parseFloat(item.custcol_ice_ld_originalrate || 0))}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                                  />
                                </td>
                                <td style={{ minWidth: '100px' }}>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm text-end"
                                    value={item.rate}
                                    onChange={e => handleItemChange(i, 'rate', e.target.value)}
                                    readOnly={orderLocked}
                                    disabled={orderLocked}
                                    style={orderLocked ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                                    step="0.01"
                                  />
                                </td>
                                <td style={{ minWidth: '100px' }}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm text-end bg-light"
                                    value={formatCurrency(parseFloat(item.custcol_ar_so_custprice || 0))}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                                  />
                                </td>
                                <td style={{ minWidth: '100px' }}>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm text-end"
                                    value={item.custcol_ice_ld_discount}
                                    onChange={e => handleItemChange(i, 'custcol_ice_ld_discount', e.target.value)}
                                    readOnly={orderLocked}
                                    disabled={orderLocked}
                                    style={orderLocked ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                                    step="0.01"
                                  />
                                </td>
                                <td style={{ minWidth: '80px' }}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm text-end bg-light"
                                    value={item.custcol_ice_ld_discountpercent ? `${parseFloat(item.custcol_ice_ld_discountpercent).toFixed(2)}%` : '0%'}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                                  />
                                </td>
                                <td style={{ minWidth: '100px' }}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm text-end bg-light"
                                    value={formatCurrency(parseFloat(item.custcol_ice_ld_discountperunit || 0))}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                                  />
                                </td>
                                <td style={{ minWidth: '150px' }}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={item.description}
                                    onChange={e => handleItemChange(i, 'description', e.target.value)}
                                    readOnly={orderLocked}
                                    disabled={orderLocked}
                                    style={orderLocked ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                                  />
                                </td>
                                <td style={{ minWidth: '120px' }}>
                                  <LocationSelect
                                    value={item.location}
                                    onChange={e => handleItemChange(i, 'location', e.target.value)}
                                    disabled={orderLocked}
                                    locations={orderData?.locations || {}}
                                    size="small"
                                  />
                                </td>
                                <td className="text-end" style={{ minWidth: '100px' }}>
                                  <span className="fw-bold text-success">{formatCurrency(itemTotal)}</span>
                                </td>
                                <td style={{ minWidth: '120px' }}>
<UnitsSelect
  value={item.units}
  onChange={(newValue) => handleUnitsChange(i, newValue)}
  disabled={orderLocked}
  itemId={item.item_id}
  size="small"
/>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                      {formData.items.length > 0 && (
                        <tfoot className="table-success">
                          <tr>
                            <th colSpan={11} className="text-end fs-6">รวมทั้งหมด:</th>
                            <th className="text-end text-success fs-5">{formatCurrency(orderSummary.totalAmount)}</th>
                            <th colSpan={1}></th>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                )}
                  </div>
                </div>
              </div>
            </div>

            {/* Department Selection - Only show if order is editable */}
            {!orderLocked && (
              <div className="row mb-4" id="departments">
                <div className="col-12">
                  <div className="theme-card">
                    <div className="theme-card-header">
                      <h5 className="mb-0">
                        <i className="bi bi-people me-2"></i>
                        เลือกพบธ.ที่ต้องการให้แจ้งเตือน
                      </h5>
                    </div>
                    <div className="theme-card-body">
                      <div className="row">
                        <div className="col-md-8">
                          <label className="form-label fw-semibold">
                            </label>
                              <Select
                                isMulti
                                options={departments}
                                value={departments.filter(d => selectedDepartments.includes(d.value))}
                                onChange={(selected) => setSelectedDepartments(selected.map(d => d.value))}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                placeholder="เลือกแผนกที่ขึ้นต้นด้วย S..."
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: base => ({ ...base, zIndex: 9999 })
                                }}
                              />
                          <div className="form-text">
                            <i className="bi bi-info-circle me-1"></i>
                            กด Ctrl (หรือ Cmd บน Mac) ค้างไว้เพื่อเลือกหลายแผนก
                          </div>
                        </div>
                        <div className="col-md-4">
                          {selectedDepartments.length > 0 && (
                            <div className="mt-4 mt-md-0">
                              <h6 className="fw-semibold mb-3">
                                <i className="bi bi-check-circle text-success me-2"></i>แผนกที่เลือก ({selectedDepartments.length})
                              </h6>
                              <div className="card bg-success bg-opacity-10 border-success">
                                <div className="card-body p-3">
                                  {selectedDepartments.map((dept, index) => (
                                    <div key={index} className="d-flex align-items-center mb-2">
                                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                                      <small className="fw-semibold">{dept}</small>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Enhanced Floating Action Buttons */}
      <div className="position-fixed bottom-0 end-0 p-4" style={{ zIndex: 1000 }}>
        <div className="d-flex flex-column gap-3">
          {/* Save Button */}
          <button 
            type="submit" 
            className="btn btn-success btn-lg shadow-lg"
            disabled={processing || orderLocked}
            onClick={handleSubmit}
            style={{ borderRadius: '50px', minWidth: '60px', height: '60px' }}
            title={orderLocked ? 'ไม่สามารถบันทึกได้' : 'บันทึกการเปลี่ยนแปลง'}
          >
            {processing ? (
              <span className="spinner-border spinner-border-sm"></span>
            ) : (
              <i className="bi bi-save" style={{ fontSize: '1.5rem' }}></i>
            )}
          </button>

          {/* NetSuite Link */}
          <a
            href={`https://7446749${so?.realm?.includes('SB') ? '-sb1' : ''}.app.netsuite.com/app/accounting/transactions/salesord.nl?id=${id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-info btn-lg shadow-lg"
            style={{ borderRadius: '50px', minWidth: '60px', height: '60px' }}
            title="เปิดใน NetSuite"
          >
            <i className="bi bi-box-arrow-up-right" style={{ fontSize: '1.5rem' }}></i>
          </a>

          {/* Back Button */}
          <button
            type="button"
            className="btn btn-secondary btn-lg shadow-lg"
            onClick={() => navigate(-1)}
            disabled={processing}
            style={{ borderRadius: '50px', minWidth: '60px', height: '60px' }}
            title="กลับไปหน้าก่อนหน้า"
          >
            <i className="bi bi-arrow-left" style={{ fontSize: '1.5rem' }}></i>
          </button>

          {/* Scroll to Top */}
          <button
            type="button"
            className="btn btn-light btn-lg shadow-lg"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{ borderRadius: '50px', minWidth: '60px', height: '60px' }}
            title="เลื่อนขึ้นด้านบน"
          >
            <i className="bi bi-arrow-up" style={{ fontSize: '1.5rem' }}></i>
          </button>
        </div>
      </div>

      {/* Remark Modal */}
      <RemarkModal
        show={showRemarkModal}
        onHide={() => setShowRemarkModal(false)}
        onSave={handleRemarkSave}
        initialValue={formData.custbodyar_so_memo2}
        conditions={orderData.conditions || {}}
        netsuiteEntityId={orderData.so?.entity?.id}
      />

      {/* Logs Modal */}
      <LogsModal
        show={showLogsModal}
        onHide={() => setShowLogsModal(false)}
        logs={logs}
      />

      {/* Loading overlay */}
      {processing && (
        <div className="global-loader">
          <div className="global-loader__content">
            <img src="/VERP.png" alt="VERP" className="global-loader__logo-img" />
            <div className="global-loader__bar">
              <div className="global-loader__bar-fill"></div>
            </div>
            <p className="global-loader__message">กำลังบันทึกการเปลี่ยนแปลง...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditOrder;
